import { MAX_PARAM_LENGTH } from './constants';

/**
 * @description
 * Turn a form or backend field path into a low-cardinality name without personal data:
 * indexes and custom field ids are dropped, e.g.
 * groupRegistrations.3.email -> groupRegistrations.email
 * registrations.0.member.custom.2 -> registrations.member.custom
 * groupRegistrations.0.custom.custom_field_123 -> groupRegistrations.custom
 */
export function normalizeField(path: string | string[]): string {
  const value = Array.isArray(path) ? path.join('.') : path;

  return value
    .split('.')
    .filter((part) => part !== '' && !/^\d+$/.test(part) && !/^custom_field_\d+$/.test(part))
    .join('.');
}

/**
 * @description
 * Unique normalized field names, comma-joined and cut to the GA4 value limit
 */
export function joinFields(paths: Array<string | string[]>): string {
  const unique = Array.from(new Set(paths.map(normalizeField).filter(Boolean)));

  return truncate(unique.join(','));
}

export function truncate(value: string): string {
  return value.length > MAX_PARAM_LENGTH ? value.slice(0, MAX_PARAM_LENGTH) : value;
}

/**
 * @description
 * Replace ids and uuids in a path so it can be grouped, e.g.
 * /api/v1/web/races/123/registrations -> /api/v1/web/races/:id/registrations
 */
export function pathTemplate(url: string | nil): string {
  if (!url) {
    return '';
  }

  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];

  return truncate(
    path
      .split('/')
      .map((part) => {
        if (/^\d+$/.test(part)) return ':id';
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(part)) return ':uuid';
        if (/^[A-Za-z0-9_-]{20,}$/.test(part)) return ':token';
        return part;
      })
      .join('/'),
  );
}

export function priceBand(price: number | nil): string {
  if (!price || price <= 0) return '0';
  if (price < 300) return '1-299';
  if (price < 600) return '300-599';
  if (price < 1000) return '600-999';
  return '1000+';
}

/**
 * Parameter names that may only be sent when listed in SAFE_PARAMS
 */
const SENSITIVE_PARAM = /value|email|name|phone|birth|address/i;

/**
 * Parameters that match SENSITIVE_PARAM but are known to be safe
 */
const SAFE_PARAMS = ['value', 'next_page'];

/**
 * @description
 * Drop parameters that could carry personal data, drop empty values and cut strings to the GA4 limit
 */
export function sanitizeParams(params: Record<string, unknown>): Record<string, string | number | boolean | object> {
  const result: Record<string, string | number | boolean | object> = {};

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (value === undefined || value === null || value === '') {
      return;
    }

    if (SENSITIVE_PARAM.test(key) && !SAFE_PARAMS.includes(key)) {
      return;
    }

    result[key] = typeof value === 'string' ? truncate(value) : (value as any);
  });

  return result;
}
